/**
 * The Markets game card (docs/markets-ux.md §2), shared by Markets (pages/ContestBrowser.tsx) and
 * the Desk's Live & soon strip (docs/desk-ux.md §2). One soft card per contest: league · start (or
 * live clock) · venue count, then the moneyline (one best price + venue per side) for a game or
 * the three shortest prices for a futures contest. Prices load lazily once the card nears the
 * viewport (a `ViewWatcher` provided through `WatcherCtx`), poll only while on screen and not
 * streamed, and open the trade ticket through `onPick`. Tapping elsewhere opens the game page.
 *
 * Where prices come from is `CardPricesCtx` (default: `useCardLiquidity`, the trimmed board
 * route), so the dev previews can render cards from mock data without the network.
 */
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Contest, League } from "@greenbook/client";
import { useCardLiquidity } from "../lib/queries.js";
import { fmtKickoff, fmtRelative } from "../lib/format.js";
import { VenueLogo } from "./VenueLogo.js";
import { PriceButton, PriceButtonSkeleton, Skeleton, SoftCard } from "./kit.js";
import {
  buildCardModel,
  isGame,
  matchupSep,
  priceStatus,
  startMs,
  teamLabel,
  teamsOf,
  type Bucket,
  type CardLiquidity,
  type CardModel,
  type CardQuote,
  type LiveSignal,
  type PriceStatus,
  type Team,
} from "../lib/marketsBoard.js";

/** The hook a card reads its prices with (same contract as `useCardLiquidity`). */
export type CardPricesHook = (
  id: string,
  game: boolean,
  opts: { enabled: boolean; poll: boolean },
) => { data?: CardLiquidity; isError: boolean; refetch: () => unknown };
export const CardPricesCtx = createContext<CardPricesHook>(useCardLiquidity);

export type StateEntry = {
  live?: boolean;
  state?: {
    game_status?: string | null;
    clock?: { display?: string | null; is_running?: boolean } | null;
    period?: { label?: string | null } | null;
    scores?: { home?: number | null; away?: number | null; home_score?: number | null; away_score?: number | null } | null;
  } | null;
};

/** Live-ness from a state entry: null when there's nothing to read. */
export function liveFromState(st: StateEntry | undefined): LiveSignal {
  if (!st) return null;
  if (st.live === true) return true;
  const gs = (st.state?.game_status ?? "").toLowerCase();
  if (!gs) return st.live === false ? false : null;
  return !gs.includes("pre") && !gs.includes("scheduled") && !gs.includes("final") && !gs.includes("complete");
}

/* ------------------------------------------------------------ viewport watcher */

/**
 * One pair of IntersectionObservers for the whole board, rooted on the scroll container (a
 * viewport-rooted observer can't see past the container's clipping, so its margin would never
 * prefetch). `near` fires once a card is within ~300px of the screen; `visible` tracks on-screen.
 */
export class ViewWatcher {
  private near: IntersectionObserver;
  private vis: IntersectionObserver;
  private handlers = new Map<Element, { near: () => void; visible: (v: boolean) => void }>();
  /** `margin` widens the "near" box (default: 300px above and below, for a vertical board). */
  constructor(root: Element, margin = "300px 0px 300px 0px") {
    this.near = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) this.handlers.get(e.target)?.near();
      },
      { root, rootMargin: margin },
    );
    this.vis = new IntersectionObserver(
      (entries) => {
        for (const e of entries) this.handlers.get(e.target)?.visible(e.isIntersecting);
      },
      { root },
    );
  }
  observe(el: Element, h: { near: () => void; visible: (v: boolean) => void }): () => void {
    this.handlers.set(el, h);
    this.near.observe(el);
    this.vis.observe(el);
    return () => {
      this.handlers.delete(el);
      this.near.unobserve(el);
      this.vis.unobserve(el);
    };
  }
  disconnect(): void {
    this.near.disconnect();
    this.vis.disconnect();
  }
}
/** Provide one ViewWatcher (rooted on the scroll container) to every GameCard below. */
export const WatcherCtx = createContext<ViewWatcher | null>(null);

function useCardViewport(ref: React.RefObject<Element>, onVisible: (v: boolean) => void) {
  const watcher = useContext(WatcherCtx);
  const [seen, setSeen] = useState(false);
  const [visible, setVisible] = useState(false);
  const cb = useRef(onVisible);
  cb.current = onVisible;
  useEffect(() => {
    const el = ref.current;
    if (!watcher || !el) return;
    const off = watcher.observe(el, {
      near: () => setSeen(true),
      visible: (v) => {
        setVisible(v);
        cb.current(v);
      },
    });
    return () => {
      off();
      cb.current(false);
    };
  }, [watcher, ref]);
  return { seen, visible };
}

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/* -------------------------------------------------------------------- card */

function startText(c: Contest, bucket: Bucket): string {
  if (bucket === "futures") return "";
  const time = fmtKickoff(c.starts_at);
  const timeOnly = time.includes(", ") ? time.split(", ")[1] : time;
  if (bucket === "soon") return `${fmtRelative(c.starts_at)} · ${timeOnly}`;
  if (bucket === "today" || bucket === "tomorrow") return timeOnly;
  return time;
}

function scoresOf(st: StateEntry | undefined): { home: number | null; away: number | null } | null {
  const s = st?.state?.scores;
  if (!s) return null;
  const home = s.home ?? s.home_score ?? null;
  const away = s.away ?? s.away_score ?? null;
  return home == null && away == null ? null : { home, away };
}

/** Memoized: every card reports its status up to the page, which must not re-render the board. */
export const GameCard = memo(function GameCard({
  contest,
  bucket,
  league,
  state,
  now,
  allowed,
  tradeableOnly,
  streamed,
  activeHash,
  onPick,
  onStatus,
  onVisible,
}: {
  contest: Contest;
  bucket: Bucket;
  league?: League;
  state?: StateEntry;
  now: number;
  allowed: ReadonlySet<string> | null;
  tradeableOnly: boolean;
  streamed: boolean;
  activeHash: string | null;
  onPick: (c: Contest, q: CardQuote) => void;
  onStatus: (id: string, s: PriceStatus) => void;
  onVisible: (id: string, v: boolean) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const visibleCb = useCallback((v: boolean) => onVisible(contest.id, v), [onVisible, contest.id]);
  const { seen, visible } = useCardViewport(ref, visibleCb);
  const useCardPrices = useContext(CardPricesCtx);
  const game = isGame(contest);
  const q = useCardPrices(contest.id, game, {
    enabled: seen && contest.has_markets !== false,
    poll: visible && !streamed,
  });
  const model: CardModel | null = useMemo(
    () => (q.data ? buildCardModel(contest, q.data, allowed) : contest.has_markets === false ? { kind: "none", tradeable: false } : null),
    [q.data, contest, allowed],
  );
  const status = priceStatus(contest, q);

  // Report status; a card that turns out empty under "tradeable only" fades out first.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (status === "empty" && tradeableOnly) {
      if (prefersReducedMotion()) {
        onStatus(contest.id, status);
        return;
      }
      setLeaving(true);
      const t = setTimeout(() => onStatus(contest.id, status), 180);
      return () => clearTimeout(t);
    }
    setLeaving(false);
    onStatus(contest.id, status);
  }, [status, tradeableOnly, contest.id, onStatus]);

  const teams = teamsOf(contest);
  const live = bucket === "live";
  const href = `/contests/${encodeURIComponent(contest.id)}`;
  const venues = model && model.kind !== "none" ? model.venues : null;

  return (
    <div ref={ref} data-contest={contest.id} className={`min-w-0 ${leaving ? "gb-card-out" : ""}`}>
      <SoftCard
        href={href}
        linkLabel={`Open ${contest.title ?? "game"}`}
        dim={model?.kind === "none"}
        highlight={live}
        className="flex h-full flex-col gap-2.5 p-3"
      >
        {/* header: league · start (or live clock) · venues */}
        <div className="pointer-events-none relative flex min-w-0 items-center gap-1.5 text-[11px] text-faint">
          <VenueLogo name={league?.name} logoUrl={league?.image_url} size={14} />
          <span className="shrink-0 font-medium uppercase tracking-wide text-dim">{league?.name ?? "—"}</span>
          <span className="min-w-0 truncate">
            <StartOrClock contest={contest} bucket={bucket} state={state} now={now} />
          </span>
          {venues != null && venues > 0 && (
            <span className="ml-auto shrink-0">
              {venues} venue{venues === 1 ? "" : "s"}
            </span>
          )}
        </div>

        {game ? (
          <GameBody
            contest={contest}
            teams={teams}
            model={model}
            status={status}
            score={live ? scoresOf(state) : null}
            activeHash={activeHash}
            onPick={(qt) => onPick(contest, qt)}
            onRetry={() => q.refetch()}
            href={href}
          />
        ) : (
          <FuturesBody
            contest={contest}
            model={model}
            status={status}
            activeHash={activeHash}
            onPick={(qt) => onPick(contest, qt)}
            onRetry={() => q.refetch()}
            href={href}
          />
        )}
      </SoftCard>
    </div>
  );
});

function StartOrClock({ contest, bucket, state, now }: { contest: Contest; bucket: Bucket; state?: StateEntry; now: number }) {
  if (bucket === "live") {
    const clock = state?.state?.clock?.display;
    const period = state?.state?.period?.label;
    const text = [period, clock].filter(Boolean).join(" · ");
    if (text) return <span className="nums font-medium text-blue">· {text}</span>;
    if (state?.live) return <span className="font-medium text-blue">· Live</span>;
    // No game state to read (signed out / no scope): say what we know.
    const ago = now - startMs(contest);
    return <span className="text-blue">· Started {Number.isFinite(ago) ? fmtRelative(contest.starts_at) : ""}</span>;
  }
  const t = startText(contest, bucket);
  return t ? <span className="nums">· {t}</span> : null;
}

/** The price-or-placeholder slot for one outcome. */
function QuoteSlot({
  quote,
  status,
  layout,
  activeHash,
  onPick,
  label,
}: {
  quote: CardQuote | null;
  status: PriceStatus;
  layout: "tile" | "row";
  activeHash: string | null;
  onPick: (q: CardQuote) => void;
  label?: ReactNode;
}) {
  if (!quote) return status === "pending" ? <PriceButtonSkeleton layout={layout} /> : null;
  const best = quote.best;
  return (
    <PriceButton
      layout={layout}
      label={label ?? (layout === "row" ? quote.label : undefined)}
      price={best?.price}
      american={best?.american_odds}
      venue={best?.partner_name ?? best?.partner_id}
      active={activeHash === quote.position.position_hash}
      disabled={!best}
      emptyText="Not on your venues"
      onClick={() => onPick(quote)}
      title={best ? `Buy ${quote.label} at ${best.partner_name ?? best.partner_id}` : "None of your connected venues quote this"}
    />
  );
}

function TeamName({ team, score }: { team?: Team; score?: number | null }) {
  return (
    <div className="pointer-events-none relative flex min-w-0 flex-1 items-center gap-2">
      <VenueLogo name={team?.name ?? team?.abbr} size={20} />
      <span className="min-w-0 break-words text-[14px] font-medium leading-snug text-ink">{team ? teamLabel(team) : "—"}</span>
      {score != null && <span className="nums ml-auto shrink-0 pr-1 text-[15px] font-semibold text-ink">{score}</span>}
    </div>
  );
}

function GameBody({
  contest,
  teams,
  model,
  status,
  score,
  activeHash,
  onPick,
  onRetry,
  href,
}: {
  contest: Contest;
  teams: { home: Team; away: Team } | null;
  model: CardModel | null;
  status: PriceStatus;
  score: { home: number | null; away: number | null } | null;
  activeHash: string | null;
  onPick: (q: CardQuote) => void;
  onRetry: () => void;
  href: string;
}) {
  const quotes = model?.kind === "moneyline" ? model.quotes : [];
  const threeWay = quotes.length >= 3;
  const quoteFor = (side: "home" | "away"): CardQuote | null => {
    if (!model || model.kind !== "moneyline") return null;
    const t = side === "home" ? teams?.home : teams?.away;
    const name = (t?.name ?? "").toLowerCase();
    return (
      quotes.find((q) => (q.position.side_key ?? "").toLowerCase() === side) ??
      quotes.find((q) => (q.position.participant_name ?? "").toLowerCase() === name) ??
      null
    );
  };
  const draw = threeWay ? (quotes.find((q) => /^(draw|tie)$/i.test(q.position.participant_name ?? "")) ?? null) : null;
  const priced = model?.kind === "moneyline";
  const slot = "w-[44%] max-w-[11rem] shrink-0";

  // No team tags: a titled matchup with its outcomes as rows.
  if (!teams) {
    return (
      <div className="flex flex-col gap-2">
        <div className="pointer-events-none relative break-words text-[14px] font-medium text-ink">{contest.title ?? "(untitled)"}</div>
        {priced ? (
          quotes.map((q) => <QuoteSlot key={q.key} quote={q} status={status} layout="row" activeHash={activeHash} onPick={onPick} />)
        ) : status === "pending" ? (
          [0, 1].map((i) => <PriceButtonSkeleton key={i} layout="row" />)
        ) : (
          <CardNote model={model} status={status} onRetry={onRetry} href={href} />
        )}
      </div>
    );
  }

  const rows: { key: string; team?: Team; label?: string; score?: number | null; quote: CardQuote | null }[] = [
    { key: "away", team: teams.away, score: score?.away, quote: quoteFor("away") },
    ...(threeWay ? [{ key: "draw", label: "Draw", quote: draw }] : []),
    { key: "home", team: teams.home, score: score?.home, quote: quoteFor("home") },
  ];

  return (
    <div className="flex flex-col gap-2">
      {rows.map((r, i) => (
        <div key={r.key} className="flex min-h-11 items-center gap-3">
          {r.team ? (
            <TeamName team={r.team} score={r.score} />
          ) : (
            <div className="pointer-events-none relative flex-1 pl-7 text-[13px] text-dim">{r.label}</div>
          )}
          {(priced || status === "pending") && (
            <div className={slot}>
              <QuoteSlot quote={r.quote} status={status} layout="tile" activeHash={activeHash} onPick={onPick} />
            </div>
          )}
          {!priced && status !== "pending" && i === 0 && (
            <div className={`${slot} self-start`}>
              <CardNote model={model} status={status} onRetry={onRetry} href={href} compact />
            </div>
          )}
        </div>
      ))}
      <span className="sr-only">
        {teamLabel(teams.away)} {matchupSep(contest)} {teamLabel(teams.home)}
      </span>
    </div>
  );
}

/** What a card says instead of prices. */
function CardNote({
  model,
  status,
  onRetry,
  href,
  compact = false,
}: {
  model: CardModel | null;
  status: PriceStatus;
  onRetry: () => void;
  href: string;
  compact?: boolean;
}) {
  const box = `relative flex min-h-11 items-center text-[12px] ${compact ? "justify-end text-right" : ""}`;
  if (status === "error") {
    return (
      <div className={box}>
        <button type="button" onClick={onRetry} className="relative z-10 text-faint hover:text-amber">
          Prices unavailable · <span className="text-amber">Retry</span>
        </button>
      </div>
    );
  }
  if (model?.kind === "elsewhere") {
    return (
      <div className={box}>
        <Link to={href} className="relative z-10 text-dim hover:text-amber">
          {model.otherMarkets} market{model.otherMarkets === 1 ? "" : "s"} on the game page →
        </Link>
      </div>
    );
  }
  return <div className={`${box} pointer-events-none text-faint`}>No prices yet</div>;
}

function FuturesBody({
  contest,
  model,
  status,
  activeHash,
  onPick,
  onRetry,
  href,
}: {
  contest: Contest;
  model: CardModel | null;
  status: PriceStatus;
  activeHash: string | null;
  onPick: (q: CardQuote) => void;
  onRetry: () => void;
  href: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="pointer-events-none relative break-words text-[14px] font-medium leading-snug text-ink">
        {contest.title ?? "(untitled)"}
      </div>
      {status === "pending" && !model ? (
        [0, 1, 2].map((i) => <PriceButtonSkeleton key={i} layout="row" />)
      ) : model?.kind === "futures" ? (
        <>
          {model.quotes.map((q) => (
            <QuoteSlot key={q.key} quote={q} status={status} layout="row" activeHash={activeHash} onPick={onPick} />
          ))}
          {model.more > 0 && (
            <Link to={href} className="relative z-10 self-start py-1 text-[12px] text-dim hover:text-amber">
              +{model.more} more →
            </Link>
          )}
        </>
      ) : (
        <CardNote model={model} status={status} onRetry={onRetry} href={href} />
      )}
    </div>
  );
}


/** A game card's loading footprint (league line + two team rows with price skeletons). */
export function GameCardSkeleton() {
  return (
    <div className="rounded-lg border border-border bg-panel-2 p-3">
      <div className="mb-3 flex items-center gap-2">
        <Skeleton className="h-4 w-4 rounded-full" />
        <Skeleton className="h-3 w-28" />
        <Skeleton className="ml-auto h-3 w-14" />
      </div>
      {[0, 1].map((r) => (
        <div key={r} className="mt-2 flex items-center gap-3">
          <Skeleton className="h-5 w-5 rounded-full" />
          <Skeleton className="h-4 w-32 max-w-[40%]" />
          <div className="ml-auto w-40">
            <PriceButtonSkeleton />
          </div>
        </div>
      ))}
    </div>
  );
}
